/**
 * Цвета темы героя. Считаем в JS, а не через color-mix(): на iOS до 16.2 color-mix нет,
 * а сайт героя открывают с телефона. Контраст — по WCAG 2.
 */

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

export interface Hsl {
  /** 0..360 */
  h: number;
  /** 0..1 */
  s: number;
  /** 0..1 */
  l: number;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/** "#abc", "#aabbcc", "#aabbccdd", "rgb(1, 2, 3)", "rgb(1 2 3 / .5)"; прозрачность отбрасывается. */
export function parseColor(input: string): Rgb | null {
  const value = input.trim().toLowerCase();
  const hex = /^#([0-9a-f]{3,8})$/.exec(value);
  if (hex) {
    const digits = hex[1] ?? "";
    if (digits.length === 3 || digits.length === 4) {
      const [r, g, b] = Array.from(digits.slice(0, 3), (d) => parseInt(d + d, 16));
      return r === undefined || g === undefined || b === undefined ? null : { r, g, b };
    }
    if (digits.length === 6 || digits.length === 8) {
      return {
        r: parseInt(digits.slice(0, 2), 16),
        g: parseInt(digits.slice(2, 4), 16),
        b: parseInt(digits.slice(4, 6), 16),
      };
    }
    return null;
  }
  const rgb = /^rgba?\(\s*(\d{1,3})[\s,]+(\d{1,3})[\s,]+(\d{1,3})/.exec(value);
  if (rgb) {
    const [r, g, b] = [rgb[1], rgb[2], rgb[3]].map((part) => clamp(Number(part), 0, 255));
    return r === undefined || g === undefined || b === undefined ? null : { r, g, b };
  }
  return null;
}

export function toHex({ r, g, b }: Rgb): string {
  const part = (channel: number) => Math.round(clamp(channel, 0, 255)).toString(16).padStart(2, "0");
  return `#${part(r)}${part(g)}${part(b)}`;
}

export function rgbToHsl({ r, g, b }: Rgb): Hsl {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return { h: 0, s: 0, l };
  const s = d / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === rn) h = ((gn - bn) / d) % 6;
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;
  return { h: (h * 60 + 360) % 360, s: clamp(s, 0, 1), l };
}

export function hslToRgb({ h, s, l }: Hsl): Rgb {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let rgb: [number, number, number];
  if (hp < 1) rgb = [c, x, 0];
  else if (hp < 2) rgb = [x, c, 0];
  else if (hp < 3) rgb = [0, c, x];
  else if (hp < 4) rgb = [0, x, c];
  else if (hp < 5) rgb = [x, 0, c];
  else rgb = [c, 0, x];
  const m = l - c / 2;
  return { r: (rgb[0] + m) * 255, g: (rgb[1] + m) * 255, b: (rgb[2] + m) * 255 };
}

/** Относительная яркость WCAG. */
export function luminance({ r, g, b }: Rgb): number {
  const linear = (channel: number) => {
    const c = channel / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
}

export function contrast(a: Rgb, b: Rgb): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Смесь: weight = 0 — чистый a, 1 — чистый b. */
export function mix(a: Rgb, b: Rgb, weight: number): Rgb {
  const w = clamp(weight, 0, 1);
  return { r: a.r + (b.r - a.r) * w, g: a.g + (b.g - a.g) * w, b: a.b + (b.b - a.b) * w };
}

/**
 * Сдвинуть светлоту цвета, пока контраст с фоном не станет не меньше min. Тон и
 * насыщенность сохраняются — акцент героя остаётся его акцентом, только темнее или светлее.
 */
export function withContrast(color: Rgb, against: Rgb, min: number): Rgb {
  if (contrast(color, against) >= min) return color;
  const hsl = rgbToHsl(color);
  const darker = luminance(against) > 0.18;
  for (let step = 1; step <= 100; step += 1) {
    const l = clamp(hsl.l + (darker ? -step : step) / 100, 0, 1);
    const candidate = hslToRgb({ ...hsl, l });
    if (contrast(candidate, against) >= min) return candidate;
    if (l === 0 || l === 1) break;
  }
  return darker ? { r: 0, g: 0, b: 0 } : { r: 255, g: 255, b: 255 };
}
